import { motion } from 'motion/react';

import { AGENTS } from '@/lib/constants';
import { sectionNumber } from '@/lib/utils';

interface Step {
  index: number;
  title: string;
  body: string;
  agents: string[];
}

const STEPS: Step[] = [
  {
    index: 1,
    title: 'Intent, from selection rather than prompt.',
    body:
      'You answer a structured questionnaire — fourteen questions across goal, audience, typography, colour, navigation, and features. ORA reads your answers and surfaces a clean intent descriptor. If anything is ambiguous, ORA asks up to one round of targeted follow-ups before continuing. You review and edit at a checkpoint.',
    agents: ['ORA'],
  },
  {
    index: 2,
    title: 'Sketches, read in web-design vocabulary.',
    body:
      'Upload up to five lo-fi sketches — hand-drawn, photographed, or digital. SCRY reads each one and extracts a component tree, a relative-position map, and a virtual DOM, naming elements with the same web-design terms a designer would use. You review and edit per page.',
    agents: ['SCRY'],
  },
  {
    index: 3,
    title: 'Synthesis, against the UX knowledge graph.',
    body:
      'LOOM merges intent and sketches against AURA\u2019s UX knowledge graph \u2014 a structured map of UX laws, heuristics, and component conventions. LEX, the validator, scores LOOM\u2019s plan against the graph and sends revisions back until critical violations are resolved. You review the final technical descriptor and design system.',
    agents: ['LOOM', 'LEX'],
  },
  {
    index: 4,
    title: 'Hi-fi mockups and a deployable codebase.',
    body:
      'LUMEN renders one hi-fi mockup per page. MOSAIC generates logos, hero imagery, and supporting assets. FORGE emits the React + Vite + Tailwind + shadcn/ui codebase in internally-reviewed batches \u2014 LEX critiques each batch against the same graph. You receive a gallery of mockups and a zipped source tree, ready to deploy.',
    agents: ['LUMEN', 'MOSAIC', 'FORGE'],
  },
];

export function HowItWorks() {
  return (
    <section id="how-it-works" className="relative scroll-mt-24 py-24">
      {/* Section header */}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-2">
          <p className="marginalia">
            <span className="mr-2">{sectionNumber(2)}</span>
            the pipeline
          </p>
        </div>
        <div className="col-span-12 md:col-span-10">
          <h2 className="font-serif text-display-md text-ink">
            Four stages.{' '}
            <span className="italic text-graphite">Four reviews.</span>
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-graphite">
            Every stage is a specialised agent. Every stage has a name, a
            role, and a narrow contract. You review between stages, editing
            or asking for revisions where it matters.
          </p>
        </div>
      </div>

      <div className="mt-16 h-px bg-ink/20" />

      {/* Steps */}
      <div className="mt-2">
        {STEPS.map((step, i) => (
          <motion.div
            key={step.index}
            className="grid grid-cols-12 gap-6 border-b border-ink/10 py-12"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Step number, extra large, editorial */}
            <div className="col-span-12 md:col-span-2">
              <span className="font-serif text-7xl leading-none text-ochre">
                {sectionNumber(step.index)}
              </span>
            </div>

            {/* Body */}
            <div className="col-span-12 md:col-span-7">
              <h3 className="font-serif text-2xl italic leading-snug text-ink md:text-3xl">
                {step.title}
              </h3>
              <p className="mt-4 text-base leading-relaxed text-graphite">
                {step.body}
              </p>
            </div>

            {/* Agents list */}
            <div className="col-span-12 md:col-span-3 md:border-l md:border-ink/15 md:pl-6">
              <p className="smallcaps text-graphite">Agents</p>
              <ul className="mt-3 space-y-1">
                {step.agents.map((name) => {
                  const agent = AGENTS.find((a) => a.name === name);
                  return (
                    <li key={name} className="font-mono text-xs text-ink">
                      <span className="text-ochre">¶</span>&nbsp;&nbsp;{name}
                      {agent ? (
                        <span className="block pl-5 text-graphite">
                          {agent.role}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Agent manifest */}
      <div className="mt-20 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-2">
          <p className="marginalia">
            <span className="mr-2">{sectionNumber(3)}</span>
            dramatis personae
          </p>
        </div>
        <div className="col-span-12 md:col-span-10">
          <h2 className="font-serif text-display-sm text-ink">
            The seven agents.
          </h2>

          <div className="mt-8 overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-ink/25">
                  <th className="smallcaps py-3 pr-4 text-graphite font-normal">Name</th>
                  <th className="smallcaps py-3 pr-4 text-graphite font-normal">Full form</th>
                  <th className="smallcaps py-3 pr-4 text-graphite font-normal">Role</th>
                </tr>
              </thead>
              <tbody>
                {AGENTS.map((agent) => (
                  <tr key={agent.id} className="border-b border-ink/10 align-top">
                    <td className="py-4 pr-4 font-mono text-sm font-medium text-ochre">
                      {agent.name}
                    </td>
                    <td className="py-4 pr-4 font-serif text-sm italic text-ink">
                      {agent.full}
                    </td>
                    <td className="py-4 pr-4 text-sm leading-relaxed text-graphite">
                      {agent.role}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
